import { Suspense, memo, useEffect, useRef, useState } from 'react';
import Events from '../../../../utilities/EventEmitter';
import { APP_EVENT_NAMES } from '../../../../signalR/constants';
import { compose } from 'recompose';
import { withUserProfile } from '../../../../userProfile/userProfileContext';
import { AppLoading } from '../../../../loaders/loaders';
import { withSnackbar } from 'notistack';
import { format } from 'date-fns';
import appConstants from '../../../../appConstants';
import { useNavigate } from 'react-router-dom';
import { setCityRoutesForScenario } from '../../../../utilities/cityRoutesSessionStorage';
const ETLProgressDialog = React.lazy(() => import('./ETLProgressDialog'));
const SummaryOverlay = React.lazy(() => import('./Summary'));

const ETLComponent = ({
  userData,
  enqueueSnackbar,
  onDataLoad,
  onETLSuccess,
  isValidFirstPush,
}) => {
  const navigate = useNavigate();
  const [state, setState] = useState({
    showLoader: false,
    showSummary: false,
    currentPlanDate: null,
    etlSummaryDetails: null
  });
  const loaderTimeoutRef = useRef(null);

  // Only clear cityRoutes in the Summary state when the cleared scenarioId matches the one currently shown.
  // This prevents switching the active scenario from removing cityRoutes of a new plan still open in the Summary.
  const clearETLCityRoutesRefs = (clearedScenarioId) => {
    setState((prev) => {
      if (!prev.etlSummaryDetails) return prev;
      const isSameScenario = prev.etlSummaryDetails.scenarioId === clearedScenarioId;
      if (!isSameScenario) return prev;
      return {
        ...prev,
        etlSummaryDetails: { ...prev.etlSummaryDetails, cityRoutes: null }
      };
    });
  };

  useEffect(() => {
    Events.on(APP_EVENT_NAMES.IBPLANNING.ETLProgress, checkAndNotifyETLProgress);
    Events.on(appConstants.Event.SHIPMENT_SELECTION_COMPLETED,handleShipmentSelectionCompleted);
    Events.on(APP_EVENT_NAMES.IBPLANNING.ClearETLCityRoutes, clearETLCityRoutesRefs);
    if (state.showLoader) {
      // Clear any existing timeout before setting a new one
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
      // Set timeout to hide loader and redirect after 1 minute
      loaderTimeoutRef.current = setTimeout(() => {
        setState((prevState) => ({
          ...prevState,
          showLoader: false
        }));
        reloadToHome();
      }, appConstants.ONE_MINUTE_TIME_OUT);
    } else {
      // Clear timeout if loader is hidden
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
    }

    return () => {
      Events.off(APP_EVENT_NAMES.IBPLANNING.ETLProgress, checkAndNotifyETLProgress);
      Events.off(appConstants.Event.SHIPMENT_SELECTION_COMPLETED, handleShipmentSelectionCompleted);
      Events.off(APP_EVENT_NAMES.IBPLANNING.ClearETLCityRoutes, clearETLCityRoutesRefs);
    };
  }, [state.showLoader, state.showSummary, userData]);

  const handleShipmentSelectionCompleted = ({ showLoader, planDate }) => {
    setState((prevState) => ({
      ...prevState,
      showLoader,
      currentPlanDate: planDate ? format(planDate, appConstants.ETL_LOADER_DATE_FORMAT) : null
    }));
  };

  const reloadToHome = () => {
    navigate('/', { replace: true });
    window.location.reload();
  };

  const checkAndNotifyETLProgress = (
    etlStatus,
    scenarioId,
    terminalCode,
    isSuccess,
    isFirstPush,
    planDate,
    etlSummary
  ) => {
    const { userProfile } = userData;
    const isFirstPushValid = isValidFirstPush?.({ isFirstPush, planDate });
    const formattedPlanDate = format(planDate, appConstants.ETL_LOADER_DATE_FORMAT);
    if (etlStatus === 'started') {
      handleETLStarted({ scenarioId, isFirstPushValid, formattedPlanDate });
    } else {
      handleETLCompleted({
        scenarioId,
        userProfileScenarioID: userProfile.scenarioID,
        isSuccess,
        isFirstPushValid,
        formattedPlanDate,
        terminalCode,
        isFirstPush,
        etlSummary
      });
    }
  };

  const handleETLStarted = ({ scenarioId, isFirstPushValid, formattedPlanDate }) => {
    if (scenarioId !== null) {
      onDataLoad?.(true);
    } else if (isFirstPushValid) {
      setState((prevState) => ({
        ...prevState,
        showLoader: true,
        currentPlanDate: formattedPlanDate
      }));
      onDataLoad?.(true, false);
    }
  };

  const handleETLCompleted = ({
    scenarioId,
    userProfileScenarioID,
    isSuccess,
    isFirstPushValid,
    formattedPlanDate,
    terminalCode,
    isFirstPush,
    etlSummary
  }) => {
    if (scenarioId !== null && userProfileScenarioID === scenarioId) {
      // Case when current plan is updated and etlCompleted event is received
      if (!isSuccess) {
        enqueueSnackbar(`Data refresh failed for ${terminalCode}`, { variant: 'error' });
      } else {
        onETLSuccess?.();
      }
      onDataLoad?.(false);
      setState((prevState) => ({
        ...prevState,
        showLoader: false
      }));
    } else if (isFirstPushValid) {
      // Case when firstPush is received for a future date and etlCompleted event is received
      if (isSuccess) {
        // On Success , Summary of the new plan should be displayed
        setCityRoutesForScenario(etlSummary.scenarioId, etlSummary.cityRoutes);
        setState((prevState) => ({
          ...prevState,
          showLoader: false,
          showSummary: true,
          etlSummaryDetails: { ...etlSummary, terminalCode }
        }));
      } else {
        enqueueSnackbar(`Data load for ${formattedPlanDate} failed`, {
          variant: 'error'
        });
        setState((prevState) => ({
          ...prevState,
          showLoader: false
        }));
      }
      onDataLoad?.(false);
    } else if (!isFirstPush && scenarioId === null) {
      // Handle cases when etl failure occurs
      enqueueSnackbar(`Data refresh failed for ${terminalCode}`, {
        variant: 'error'
      });
      setState((prevState) => ({
        ...prevState,
        showLoader: false,
        showSummary: false,
        currentPlanDate: null,
        etlSummaryDetails: null
      }));
    }
  };

  const closeSummary = () => {
    setState((prevState) => ({
      ...prevState,
      showSummary: false
    }));
  };

  const { showLoader, showSummary, currentPlanDate, etlSummaryDetails } = state;

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        {showLoader && (
          <ETLProgressDialog
            show={showLoader}
            planDate={currentPlanDate}
          />
        )}
        {showSummary && (
          <SummaryOverlay
            open
            handleClose={closeSummary}
            etlSummaryDetails={etlSummaryDetails}
          />
        )}
      </Suspense>
    </>
  );
};

export default compose(withUserProfile, withSnackbar)(memo(ETLComponent));
